import React, { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { useAuth } from "../../context/AuthContext";
import {
  HomeIcon,
  UsersIcon,
  MicrophoneIcon,
  CogIcon,
  ChevronDownIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  UserGroupIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isManager, isSalesperson } = useAuth();
  const location = useLocation();

  // Navigation based on user type
  const managerNavigation = [
    { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
    { name: "Upload Call", href: "/upload", icon: MicrophoneIcon },
    { name: "All Calls", href: "/team", icon: UsersIcon },
    { name: "Salespeople", href: "/employee-management", icon: UserGroupIcon },
    { name: "AI Settings", href: "/ai-settings", icon: CogIcon },
  ];

  const salespersonNavigation = [
    {
      name: "My Performance",
      href: `/salesperson/${user?.salespersonId}`,
      icon: ChartBarIcon,
    },
    { name: "Upload Call", href: "/upload", icon: MicrophoneIcon },
    { name: "Profile", href: "/profile", icon: UserIcon },
  ];

  const navigation = isManager ? managerNavigation : salespersonNavigation;

  const isCurrentPage = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left side - Logo and main navigation */}
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-indigo-600">savac+</h1>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isCurrentPage(item.href)
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right side - User menu */}
            <div className="flex items-center">
              <Menu as="div" className="ml-3 relative">
                <div>
                  <Menu.Button className="bg-white flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-full">
                    <span className="sr-only">Open user menu</span>
                    <div className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-50 transition-colors">
                      <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-indigo-600">
                          {user?.firstName?.charAt(0)}
                          {user?.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="text-left">
                        <span className="block text-sm font-medium text-gray-700">
                          {user?.firstName} {user?.lastName}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {isManager ? "Manager" : "Salesperson"}
                        </span>
                      </div>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    {isSalesperson && (
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={`${
                              active ? "bg-gray-100" : ""
                            } flex items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            <UserIcon className="h-4 w-4 mr-2" />
                            Profile Settings
                          </Link>
                        )}
                      </Menu.Item>
                    )}
                    {isManager && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/account"
                              className={`${
                                active ? "bg-gray-100" : ""
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <CreditCardIcon className="h-4 w-4 mr-2" />
                              Account Management
                            </Link>
                          )}
                        </Menu.Item>
                      </>
                    )}
                    {isManager && (
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/employee-management"
                            className={`${
                              active ? "bg-gray-100" : ""
                            } flex items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            <UserGroupIcon className="h-4 w-4 mr-2" />
                            Employee Management
                          </Link>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`${
                            active ? "bg-gray-100" : ""
                          } flex items-center w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>

          {/* Mobile navigation */}
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      isCurrentPage(item.href)
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
